import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type ColumnVisibilityItem = {
  id: string;
  header: string;
  visible: boolean;
  hideable: boolean;
};

type Props = {
  columns: ColumnVisibilityItem[];
  onToggle: (id: string) => void;
  onReset: () => void;
  disabled?: boolean;
};

export default function ColumnVisibilityMenu({ columns, onToggle, onReset, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const width = 280;
    const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
    setPosition({ top: rect.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    function onScroll() {
      updatePosition();
    }
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const hideableColumns = columns.filter((c) => c.hideable);

  return (
    <div className="column-menu">
      <button
        ref={buttonRef}
        type="button"
        className="column-menu__button btn btn-sm btn-ghost"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        disabled={disabled || hideableColumns.length === 0}
        onClick={() => setOpen((prev) => !prev)}
      >
        Columns
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={menuId}
            className="column-menu__popover app-menu__panel"
            role="menu"
            aria-label="Column visibility"
            style={{ top: position.top, left: position.left }}
          >
            <div className="column-menu__header">Columns</div>
            <div className="column-menu__list">
              {hideableColumns.map((col) => (
                <button
                  key={col.id}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={col.visible}
                  className="column-menu__item"
                  onClick={() => onToggle(col.id)}
                >
                  <input
                    type="checkbox"
                    className="column-menu__checkbox"
                    checked={col.visible}
                    readOnly
                    tabIndex={-1}
                    aria-hidden
                  />
                  <span className="column-menu__label">{col.header || col.id}</span>
                </button>
              ))}
            </div>
            <div className="column-menu__footer">
              <button type="button" className="btn btn-sm btn-ghost" onClick={onReset}>
                Reset
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
